/**
 * Admin — Vibe Shared Zone
 *
 * Operator levers for the ONE Cloudflare zone every Vibe tenant's public
 * hostnames hang off. Nothing here is tenant-scoped, and that is the whole
 * reason it is a separate surface from `ZAdminVibeTenantCluster`: these bodies
 * carry no `organizationId` because there is no single tenant to name — a
 * change here lands on the whole fleet at once.
 *
 * Today that is one endpoint, `EnableTotalTls` (NEX-3136), the repair for
 * `VIBE_ZONE_TOTAL_TLS_DISABLED`. Tenant hostnames are two labels deep under the
 * shared domain, which Universal SSL does not cover, so Total TLS is the only
 * thing issuing their edge certificates. Turning it off does not revoke the
 * certificates it already issued — they keep verifying until they expire, up to
 * 90 days — so the symptom of a disabled zone arrives a quarter after the cause,
 * all at once, on a day nobody changed anything.
 */

import { z } from "zod";

import { unlistedDiscriminantSchema } from "../../../shared/core/discriminated-union-read.schema";
import { ApiSuccessSchema } from "../../common/response.schema";

/**
 * Why the operator is re-enabling it. REQUIRED, unlike most reasons on this
 * surface, and the strictness is deliberate: nothing in the platform ever
 * disables Total TLS, so it was off because a person turned it off. Re-enabling
 * it is therefore always overriding somebody, and the audit row needs to say on
 * what basis. Same 500-char ceiling as every other operator reason.
 */
export const AdminVibeZoneEnableTotalTlsBodySchema = z
  .object({
    reason: z.string().min(1).max(500)
  })
  .strict();

/**
 * The outcome of one press. Mirrors `EnableVibeZoneTotalTlsOutcome` in
 * `apps/backend/src/vibe/application/use-cases/enable-vibe-zone-total-tls.use-case.ts`;
 * exhaustiveness is enforced at the controller boundary, so a new variant is a
 * TS error here before the controller can return it.
 *
 * SIX VARIANTS, AND FIVE OF THEM ARE NOT SUCCESS. That ratio is the point. The
 * one failure mode this whole check exists to prevent is a fleet-wide
 * certificate outage discovered 90 days late, so an operator who presses this
 * and walks away must never be told "done" by anything short of the API itself
 * reading the setting back as on:
 *
 *   - `enabled`         — it was off, the write went through, and a RE-READ
 *                         confirms it is now on. The only success.
 *   - `already_enabled` — it was already on; no write was attempted. Benign,
 *                         and the expected result of a second press.
 *   - `not_configured`  — this environment has no Cloudflare token or zone id
 *                         (local dev). Nothing was attempted.
 *   - `could_not_read`  — the setting could not be READ, so the write was
 *                         deliberately NOT attempted. Almost always a token
 *                         missing zone-scoped `SSL and Certificates Read`.
 *   - `write_failed`    — the read said disabled, the write itself was rejected.
 *                         Almost always a token missing the matching
 *                         `SSL and Certificates Write` group.
 *   - `not_confirmed`   — the write was ACCEPTED and the re-read still does not
 *                         say enabled. The dangerous one: Cloudflare returned
 *                         success and the setting did not move. Never report
 *                         this as done.
 */
export const AdminVibeZoneEnableTotalTlsOutcomeSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("enabled"),
    /** The issuing CA the zone reports after the flip, when it names one. */
    certificateAuthority: z.string().nullable()
  }),
  z.object({
    kind: z.literal("already_enabled"),
    certificateAuthority: z.string().nullable()
  }),
  z.object({ kind: z.literal("not_configured") }),
  z.object({ kind: z.literal("could_not_read"), reason: z.string() }),
  z.object({ kind: z.literal("write_failed"), reason: z.string() }),
  z.object({ kind: z.literal("not_confirmed"), reason: z.string() })
]);

/** `AdminVibeZoneEnableTotalTlsOutcome` as a reader parses it: a `kind` a newer backend added reads as the server's own word. */
export const AdminVibeZoneEnableTotalTlsOutcomeReadSchema = z.union([
  AdminVibeZoneEnableTotalTlsOutcomeSchema,
  z.looseObject({ kind: unlistedDiscriminantSchema(AdminVibeZoneEnableTotalTlsOutcomeSchema) })
]);

export const ZAdminVibeZone = {
  EnableTotalTls: {
    method: "POST" as const,
    path: "/api/admin/vibe/zone/enable-total-tls",
    Body: AdminVibeZoneEnableTotalTlsBodySchema,
    Response: ApiSuccessSchema(AdminVibeZoneEnableTotalTlsOutcomeReadSchema)
  }
} as const;

export type AdminVibeZoneEnableTotalTlsBody = z.infer<typeof AdminVibeZoneEnableTotalTlsBodySchema>;
export type AdminVibeZoneEnableTotalTlsOutcome = z.infer<
  typeof AdminVibeZoneEnableTotalTlsOutcomeSchema
>;
